import { forceUpdate, h, Host } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { renderStyles } from "./slider-style-tags";
import Swiper from "swiper";
import { Autoplay, EffectCards, EffectFade, Grid, Navigation, Pagination } from "swiper/modules";
import { adjustSliderBreakpoints } from "./adjustsSliderBreakpoints";
import { i18nProvider } from "@trumpf/ux.i18n";
Swiper.use([Pagination, Navigation, Autoplay, EffectFade, EffectCards, Grid]);
let sliderIdCounter = 0;
/**
 * @slot (default) - your slides (must each be wrapped in <ux-slider-slide>)
 * @slot pagination - for the <ux-slider-pagination> element
 */
export class Slider {
    constructor() {
        this.arrowsOffsetTop = undefined;
        this.hasOverflowingSlides = true;
        this.isBeginning = false;
        this.isEnd = false;
        this.autoplay = false;
        this.autoplayTime = 5000;
        this.pauseOnHover = false;
        this.loop = false;
        this.noArrows = false;
        this.arrowsFollowImg = false;
        this.noDrag = false;
        this.effect = "slide";
        this.speed = 400;
        this.visibleSlidesXl = 1;
        this.visibleSlidesL = 1;
        this.visibleSlidesM = 1;
        this.visibleSlidesS = 1;
        this.breakpointL = 860;
        this.breakpointM = 768;
        this.breakpointS = 567;
        this.spaceBetweenXl = 0;
        this.spaceBetweenL = 0;
        this.spaceBetweenM = 0;
        this.spaceBetweenS = 0;
        this.containerWidthXl = "100%";
        this.containerWidthL = "100%";
        this.containerWidthM = "100%";
        this.containerWidthS = "100%";
        this.slidesPerGroupXl = 1;
        this.slidesPerGroupL = 1;
        this.slidesPerGroupM = 1;
        this.slidesPerGroupS = 1;
        this.containerClasses = "";
        this.fadedSides = false;
        this.slidesOverflowVisible = false;
        this.slidesAutoHeight = false;
        this.autoHeight = false;
        this.reinitOnResize = false;
        this.startAtRandom = false;
        this.direction = "horizontal";
        this.slidesPerColumnFill = "row";
        this.slidesPerColumnXl = 1;
        this.slidesPerColumnL = 1;
        this.slidesPerColumnM = 1;
        this.slidesPerColumnS = 1;
        this.options = {
            slideClass: "ux-slider-slide",
            slidePrevClass: "ux-slider-slide-prev",
            slideActiveClass: "ux-slider-slide-active",
            slideVisibleClass: this.visibleCl,
            slideFullyVisibleClass: this.fullyVisibleCl,
            slideNextClass: "ux-slider-slide-next",
            wrapperClass: this.baseClass + "-wrapper",
            containerModifierClass: this.baseClass + "-container-",
            simulateTouch: !this.noDrag,
            effect: this.effect,
            fadeEffect: { crossFade: true },
            cardsEffect: {
                rotate: false,
                slideShadows: true,
                perSlideOffset: 15,
                perSlideRotate: 0
            },
            observer: true,
            autoplay: this.autoplay ? { delay: this.autoplayTime } : false,
            loop: this.loop,
            autoHeight: this.autoHeight,
            speed: this.speed,
            direction: this.direction,
            pagination: {
                el: null,
                clickable: true
            },
            grid: {
                rows: this.slidesPerColumnS,
                fill: this.slidesPerColumnFill
            },
            slidesPerView: this.visibleSlidesS,
            spaceBetween: this.spaceBetweenS,
            slidesPerGroup: this.slidesPerGroupS,
            watchSlidesProgress: true,
            breakpoints: {
                [this.breakpointS - this.safariSidebarAdjustment + 1]: {
                    slidesPerView: this.visibleSlidesM,
                    spaceBetween: this.spaceBetweenM,
                    slidesPerGroup: this.slidesPerGroupM,
                    grid: {
                        rows: this.slidesPerColumnM
                    }
                },
                [this.breakpointM - this.safariSidebarAdjustment + 1]: {
                    slidesPerView: this.visibleSlidesL,
                    spaceBetween: this.spaceBetweenL,
                    slidesPerGroup: this.slidesPerGroupL,
                    grid: {
                        rows: this.slidesPerColumnL
                    }
                },
                [this.breakpointL - this.safariSidebarAdjustment + 1]: {
                    slidesPerView: this.visibleSlidesXl,
                    spaceBetween: this.spaceBetweenXl,
                    slidesPerGroup: this.slidesPerGroupXl,
                    grid: {
                        rows: this.slidesPerColumnXl
                    }
                }
            },
            on: {
                init: () => {
                    this.handleArrowsPosition();
                    this.sliderInitialised.emit({
                        id: this.sliderId
                    });
                },
                paginationRender: () => {
                    this.updatePagination.emit({
                        id: this.sliderId,
                        event: "paginationRender"
                    });
                },
                slideNextTransitionStart: () => {
                    this.updatePagination.emit({
                        id: this.sliderId,
                        event: "slideNextChangeStart"
                    });
                },
                slidePrevTransitionStart: () => {
                    this.updatePagination.emit({
                        id: this.sliderId,
                        event: "slidePrevChangeStart"
                    });
                },
                slideChange: () => {
                    this.checkSliderStartEnd();
                },
                autoplayTimeLeft: (s, time, progress) => {
                    const activeBullet = this.slEl.querySelector(".swiper-pagination-bullet-active");
                    const progressBars = this.slEl.querySelectorAll(".swiper-pagination-bullet:not(.swiper-pagination-bullet-active) .swiper-pagination-progressbar");
                    if (progressBars.length) {
                        progressBars.forEach((bullet) => {
                            bullet.style.width = "0%";
                        });
                    }
                    if (activeBullet) {
                        const progressBar = activeBullet.querySelector(".swiper-pagination-progressbar");
                        if (progressBar) {
                            progressBar.style.width = `${(1 - progress) * 100}%`;
                        }
                    }
                }
            }
        };
        this.handleDuplicatesFlag = false;
    }
    /** the instance of the swiper slider */
    swiper;
    slEl;
    /** base class of the slider component */
    baseClass = "ux-slider";
    /** slider components references */
    swiperContainer;
    /** the unique id of the slider, can only contain letters and numbers */
    sliderId = `${this.baseClass}-id-${sliderIdCounter++}`;
    pauseOnHoverInitialized = false;
    /** Known bug on safari: the sidebar is not taken into consideration by swiper
     * in the calculation of breakpoints, therefore we need to "help" it with a manual
     * adjustment. The value holds the width of a possible sidebar in Safari.
     */
    safariSidebarAdjustment = adjustSliderBreakpoints();
    imgLazyCl = "ux-slider-lazy-img";
    visibleCl = "ux-slider-slide-visible";
    fullyVisibleCl = "ux-slider-slide-fully-visible";
    /** event to go to previous slide */
    updatePagination;
    /** event emitted when the slider is initialized */
    sliderInitialised;
    /** Updates the number of slides per view of the slider
     * it expects a number as argument
     */
    async updateSlidesPerView(slidesPerView) {
        if (this.swiper === undefined) {
            return;
        }
        this.swiper.params.slidesPerView = slidesPerView;
        this.swiper.update();
    }
    /** Updates the slider with the native update() method from swiper
     * It works effectively only for minor updates (size, spacing etc.)
     */
    async updateSoft() {
        if (this.swiper === undefined) {
            return;
        }
        this.swiper.update();
    }
    /** Update method to be used to reset and update the slider completely.
     * It can receive an optional argument with valid swiper options.
     * See Swiper API for more information of valid options.
     */
    async update(additionalOptions = {}) {
        if (this.swiper === undefined) {
            return;
        }
        this.swiper.destroy();
        this.swiper = new Swiper(this.swiperContainer, {
            ...this.options,
            ...additionalOptions
        });
    }
    /** Adds the slide passed as an argument to the slider. */
    async addSlide(slide, before = false) {
        if (this.swiper === undefined) {
            return;
        }
        if (before) {
            this.swiper.prependSlide(slide);
        }
        else {
            this.swiper.appendSlide(slide);
        }
    }
    /** Removes the slide that has the index specified as the argument from the slider */
    async removeSlide(index) {
        if (typeof index !== "number") {
            return;
        }
        this.swiperContainer?.querySelectorAll(".ux-slider-wrapper > ux-slider-slide")[index]?.remove();
        this.update();
    }
    /** go to next slide */
    async slideNext() {
        if (this.swiper === undefined) {
            return;
        }
        this.swiper.slideNext();
    }
    /** go to previous slide */
    async slidePrev() {
        if (this.swiper === undefined) {
            return;
        }
        this.swiper.slidePrev();
    }
    /** slide to a specific slide */
    async slideTo(index, velocity = 0) {
        if (this.swiper === undefined) {
            return;
        }
        this.swiper.slideTo(index, velocity);
    }
    sliderStopAutoplay(event) {
        if (this.swiper === undefined) {
            return;
        }
        if (event.detail === this.sliderId) {
            this.swiper.autoplay.stop();
        }
    }
    sliderResumeAutoplay(event) {
        if (this.swiper === undefined) {
            return;
        }
        if (event.detail === this.sliderId || event.detail !== undefined) {
            this.swiper.autoplay.start();
        }
    }
    nextSlideHandler(event) {
        if (this.swiper === undefined) {
            return;
        }
        if (event.detail === this.sliderId) {
            this.swiper.slideNext();
        }
    }
    previousSlideHandler(event) {
        if (this.swiper === undefined) {
            return;
        }
        if (event.detail === this.sliderId) {
            this.swiper.slidePrev();
        }
    }
    onImagesReady = debounce(() => {
        this.updatePagination.emit({
            id: this.sliderId,
            event: "imagesReady"
        });
        this.handleArrowsPosition();
    }, 100);
    /**
     * this function tries to mimic the behavior of the old image lazy loading of swiper which was removed in version 9
     */
    initLazyImages = () => {
        const hasLazyImages = this.slEl.querySelector(`img.${this.imgLazyCl}`) !== null;
        if (hasLazyImages) {
            this.slEl.querySelectorAll(`img.${this.imgLazyCl}`).forEach((img) => {
                img.addEventListener("load", this.onImagesReady);
                img.setAttribute("loading", "lazy");
                if (img.getAttribute("data-src") !== null) {
                    // @ts-expect-error Check for typing
                    img.setAttribute("src", img.getAttribute("data-src"));
                }
            });
            this.slEl.querySelectorAll(`source.${this.imgLazyCl}`).forEach((source) => {
                if (source.getAttribute("data-srcset") !== null) {
                    // @ts-expect-error Check for typing
                    source.setAttribute("srcset", source.getAttribute("data-srcset"));
                }
            });
        }
        else {
            this.onImagesReady();
        }
    };
    componentWillLoad() {
        i18nProvider.subscribe(() => {
            forceUpdate(this);
        });
        const paginationElement = this.slEl.querySelector("ux-slider-pagination");
        if (paginationElement !== null) {
            paginationElement.sliderId = this.sliderId;
        }
        this.initLazyImages();
        this.initPauseOnHover();
    }
    componentDidLoad() {
        const paginationElement = this.slEl.querySelector("ux-slider-pagination");
        let paginationLabel = false;
        let paginationProgress = false;
        if (paginationElement !== null) {
            paginationLabel = !!paginationElement.label;
            paginationProgress = !!paginationElement.progress;
        }
        this.options.pagination = {
            // @ts-expect-error Check for typing
            ...this.options.pagination,
            el: `.ux-slider-pagination[slider-id="${this.sliderId}"] .ux-slider-pagination__container`,
            renderBullet: function (index, className) {
                const slide = this.slides[index];
                const label = slide.label || index + 1;
                let bulletClass = className;
                let bulletContent = "";
                if (paginationLabel) {
                    bulletClass += " swiper-pagination-bullet--labeled";
                    bulletContent += `<div class="ux-slider-pagination-label ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">${label}</div>`;
                }
                if (paginationProgress) {
                    bulletContent += `<div class="swiper-pagination-progressbar"></div>`;
                }
                return `<span class="${bulletClass}">${bulletContent}</span>`;
            }
        };
        this.options.navigation = !this.noArrows
            ? {
                nextEl: `.ux-slider[slider-id="${this.sliderId}"] .${this.baseClass}-button-next`,
                prevEl: `.ux-slider[slider-id="${this.sliderId}"] .${this.baseClass}-button-prev`
            }
            : {
                nextEl: null,
                prevEl: null
            };
        this.swiper = new Swiper(this.swiperContainer, this.options);
        this.calculateSlidesOverflow();
        if (this.startAtRandom) {
            const randomPosition = Math.floor(Math.random() * (this.swiper.slides.length - 1) + 0);
            this.swiper.slideTo(randomPosition, 0);
        }
        if (!this.withIntObs) {
            return;
        }
        this.checkSliderStartEnd();
    }
    handleArrowsPosition = () => {
        if (this.arrowsFollowImg === false) {
            return;
        }
        // @ts-expect-error Check for typing
        let activeSlide = this.slEl.querySelector(`.ux-slider-slide-active`);
        if (activeSlide === null) {
            // @ts-expect-error Check for typing
            activeSlide = this.slEl.querySelector(`.ux-slider-slide`);
        }
        if (activeSlide === null) {
            return;
        }
        const selector = typeof this.arrowsFollowImg === "string" && this.arrowsFollowImg.length > 0 ? this.arrowsFollowImg : "img";
        const slideImage = activeSlide.querySelector(selector);
        if (slideImage === null) {
            return;
        }
        const activeSlideStyles = window.getComputedStyle(activeSlide);
        let activeSlideMarginTop = parseFloat(activeSlideStyles.getPropertyValue("margin-top"));
        if (isNaN(activeSlideMarginTop)) {
            activeSlideMarginTop = 0;
        }
        const slideImageOffsetTop = slideImage.offsetTop >= 0
            ? slideImage.offsetTop
            : slideImage.parentNode.offsetTop + slideImage.offsetTop;
        const slideImageOffsetHeight = slideImage.offsetHeight / 2;
        const arrowsOffsetTop = slideImageOffsetTop + slideImageOffsetHeight + activeSlideMarginTop;
        if (this.arrowsOffsetTop === arrowsOffsetTop) {
            return;
        }
        this.arrowsOffsetTop = arrowsOffsetTop;
    };
    checkSliderStartEnd = () => {
        if (this.swiper && !this.noArrows && !this.loop) {
            this.isBeginning = !!this.swiper.isBeginning;
            this.isEnd = !!this.swiper.isEnd;
            this.updatePagination.emit({
                id: this.sliderId,
                event: "checkSliderStartEnd",
                isBeginning: this.isBeginning,
                isEnd: this.isEnd
            });
        }
    };
    onResize = debounce(async () => {
        if (this.reinitOnResize) {
            await this.update();
            this.handleArrowsPosition();
            this.calculateSlidesOverflow();
            return;
        }
        if (typeof this.swiper !== "undefined") {
            this.swiper.update();
        }
        this.handleArrowsPosition();
        this.calculateSlidesOverflow();
    }, 200);
    handleMouseActions = (mouseAction) => (e) => {
        if (this.swiper === undefined) {
            return;
        }
        if (typeof this.pauseOnHover === "string" && this.pauseOnHover.length > 0) {
            const pauseElement = document.querySelector(`.${this.pauseOnHover}`);
            if (pauseElement === null) {
                return;
            }
            if (e.target.closest(`.${this.pauseOnHover}`) !== null) {
                if (mouseAction === "over") {
                    this.swiper.autoplay.pause();
                }
            }
            else if (this.swiper.autoplay.paused) {
                this.swiper.autoplay.resume();
            }
        }
        else {
            if (mouseAction === "over") {
                this.swiper.autoplay.pause();
            }
            else if (mouseAction === "leave" && this.swiper.autoplay.paused) {
                this.swiper.autoplay.resume();
            }
        }
    };
    handleMouseOver = this.handleMouseActions("over");
    handleMouseLeave = this.handleMouseActions("leave");
    initPauseOnHover = () => {
        if (this.pauseOnHover !== false && this.slEl !== undefined && !this.pauseOnHoverInitialized) {
            this.slEl.addEventListener("mouseover", this.handleMouseOver);
            this.slEl.addEventListener("mouseleave", this.handleMouseLeave);
            this.pauseOnHoverInitialized = true;
        }
    };
    connectedCallback() {
        this.initPauseOnHover();
        window.addEventListener("resize", this.onResize);
    }
    disconnectedCallback() {
        if (this.pauseOnHover !== false && this.pauseOnHoverInitialized) {
            this.slEl.removeEventListener("mouseover", this.handleMouseOver);
            this.slEl.removeEventListener("mouseleave", this.handleMouseLeave);
            this.pauseOnHoverInitialized = false;
        }
        window.removeEventListener("resize", this.onResize);
    }
    calculateSlidesOverflow = async () => {
        const slides = this.swiperContainer.querySelectorAll(".ux-slider-slide:not(.ux-slider-slide-duplicate)");
        const visibleSlides = this.swiperContainer.querySelectorAll(`.${this.fullyVisibleCl}`);
        this.hasOverflowingSlides = slides.length > visibleSlides.length;
        if (typeof this.swiper !== "undefined") {
            if (!this.hasOverflowingSlides && (this.loop || this.autoplay || this.pauseOnHover)) {
                await this.update({
                    loop: false,
                    autoplay: false,
                    pauseOnHover: false
                });
            }
            this.swiper.allowTouchMove = this.hasOverflowingSlides;
        }
        if (this.handleDuplicatesFlag) {
            return;
        }
        this.handleDuplicatesFlag = this.hasOverflowingSlides;
    };
    withIntObs = "IntersectionObserver" in window;
    /** END: loading performance optimization */
    render() {
        const { baseClass, noArrows, sliderId, fadedSides, containerClasses, slidesOverflowVisible, slidesAutoHeight, hasOverflowingSlides } = this;
        const sliderContainerClasses = [`${baseClass}__inner-container`];
        if (containerClasses.length > 0) {
            sliderContainerClasses.push(containerClasses);
        }
        const { t } = i18nProvider;
        return (h(Host, { key: '30221cb09474789efaa32a1bf4345892d7883e10', "slider-id": sliderId, class: {
                [baseClass]: true,
                [`${baseClass}--faded-sides`]: fadedSides,
                [`${baseClass}--slides-overflow-visible`]: slidesOverflowVisible || this.effect === "cards",
                [`${baseClass}--slides-auto-height`]: slidesAutoHeight,
                [`${baseClass}--cards`]: this.effect === "cards"
            } }, h("div", { key: '46c81383eab57460e18db96205b0d4af14e1063b', class: `${baseClass}__content-container` }, fadedSides && (h("div", { key: '26d9296696af441403c212ad2553ac9d3e59b2d4', class: `${baseClass}__faded-side ${baseClass}__faded-side--left`, style: {
                visibility: hasOverflowingSlides ? "inherit" : "hidden"
            }, onClick: () => this.swiper?.slidePrev() })), fadedSides && (h("div", { key: '21a4b3e4230672e51bc560452c15a4530137b6e4', class: `${baseClass}__faded-side ${baseClass}__faded-side--right`, style: {
                visibility: hasOverflowingSlides ? "inherit" : "hidden"
            }, onClick: () => this.swiper?.slideNext() })), renderStyles(this), h("div", { key: 'aab142d067e72890ff89359c1e0fe44470780383', class: sliderContainerClasses.join(" ") }, h("div", { key: '3d2d0a8199e982cfede8666160809fca8ab216ca', class: {
                [`${baseClass}-container`]: true,
                [`${baseClass}-container-fade`]: this.effect === "fade"
            },
            // @ts-expect-error Check for typing
            ref: (el) => (this.swiperContainer = el) }, h("div", { key: '90fd96bf5c7d944913f5b49c4f3ac575afb82786', class: `${baseClass}-wrapper` }, h("slot", { key: 'c328578fcf69b43ca8713afe406e5e21b942dd4a' })))), !noArrows && this.hasOverflowingSlides && (h("button", { key: '2208c16a174bb66642f5a055a43934ceac345cdf', class: `${baseClass}-button-prev ux-button ux-button--icon-only ux-slider-button`, type: "button", "aria-label": t("slider.prevButton.label"), ...(this.isBeginning && { "aria-disabled": "true" }) }, h("ux-icon", { key: '14ebe9f267128fb7e85a62b79a0c9a56de5d0ac0', name: "chevron-left", weight: "light" }))), !noArrows && this.hasOverflowingSlides && (h("button", { key: '0640167c17cd3736f717d5cee20f565859ed004f', class: `${baseClass}-button-next ux-button ux-button--icon-only ux-slider-button`, type: "button", "aria-label": t("slider.nextButton.label"), ...(this.isEnd && { "aria-disabled": "true" }) }, h("ux-icon", { key: '57dfc9a520457d28b79deb14ec89440c99467274', name: "chevron-right", weight: "light" })))), h("slot", { key: '57e5c51c64d04e602b6d2d1dac7e3effdcb43f8e', name: "pagination" })));
    }
    static get is() { return "ux-slider"; }
    static get originalStyleUrls() {
        return {
            "$": ["slider.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["slider.css"]
        };
    }
    static get properties() {
        return {
            "autoplay": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the slider has autoplay on"
                },
                "attribute": "autoplay",
                "reflect": false,
                "defaultValue": "false"
            },
            "autoplayTime": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the slider has autoplay on"
                },
                "attribute": "autoplay-time",
                "reflect": false,
                "defaultValue": "5000"
            },
            "pauseOnHover": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "boolean | string",
                    "resolved": "boolean | string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the slider will pause when there the mouse hovers the slider\ncan also be a CSS class selector, in which case the slider autoplay will stop when\nthe user hovers an element in the slider with the specified class."
                },
                "attribute": "pause-on-hover",
                "reflect": false,
                "defaultValue": "false"
            },
            "loop": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, set the sliding to be infinite. Note This can cause issues with certain web-components.\nIf you encounter such problems, disable loop."
                },
                "attribute": "loop",
                "reflect": false,
                "defaultValue": "false"
            },
            "noArrows": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the slider will have no navigation arrows"
                },
                "attribute": "no-arrows",
                "reflect": false,
                "defaultValue": "false"
            },
            "arrowsFollowImg": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "boolean | string",
                    "resolved": "boolean | string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "set the arrow navigation to be in the middle of the image, instead of the whole slider"
                },
                "attribute": "arrows-follow-img",
                "reflect": false,
                "defaultValue": "false"
            },
            "noDrag": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the swiping will be disabled for mouse drags"
                },
                "attribute": "no-drag",
                "reflect": false,
                "defaultValue": "false"
            },
            "effect": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"slide\" | \"fade\" | \"cube\" | \"coverflow\" | \"flip\" | \"cards\"",
                    "resolved": "\"cards\" | \"coverflow\" | \"cube\" | \"fade\" | \"flip\" | \"slide\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "slide transition effect: one of: \"slide\", \"fade\", \"cube\", \"coverflow\", \"flip\""
                },
                "attribute": "effect",
                "reflect": false,
                "defaultValue": "\"slide\""
            },
            "speed": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the speed of the slide transitions"
                },
                "attribute": "speed",
                "reflect": false,
                "defaultValue": "400"
            },
            "visibleSlidesXl": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "number | \"auto\"",
                    "resolved": "\"auto\" | number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the number of visible slides on XL viewports"
                },
                "attribute": "visible-slides-xl",
                "reflect": false,
                "defaultValue": "1"
            },
            "visibleSlidesL": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "number | \"auto\"",
                    "resolved": "\"auto\" | number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the number of visible slides on large viewports"
                },
                "attribute": "visible-slides-l",
                "reflect": false,
                "defaultValue": "1"
            },
            "visibleSlidesM": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "number | \"auto\"",
                    "resolved": "\"auto\" | number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the number of visible slides on medium viewports"
                },
                "attribute": "visible-slides-m",
                "reflect": false,
                "defaultValue": "1"
            },
            "visibleSlidesS": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "number | \"auto\"",
                    "resolved": "\"auto\" | number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the number of visible slides on small viewports"
                },
                "attribute": "visible-slides-s",
                "reflect": false,
                "defaultValue": "1"
            },
            "breakpointL": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the custom breakpoint for large viewports"
                },
                "attribute": "breakpoint-l",
                "reflect": false,
                "defaultValue": "860"
            },
            "breakpointM": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the custom breakpoint for medium viewports"
                },
                "attribute": "breakpoint-m",
                "reflect": false,
                "defaultValue": "768"
            },
            "breakpointS": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the custom breakpoint for small viewports"
                },
                "attribute": "breakpoint-s",
                "reflect": false,
                "defaultValue": "567"
            },
            "spaceBetweenXl": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the space between slides for XL viewports"
                },
                "attribute": "space-between-xl",
                "reflect": false,
                "defaultValue": "0"
            },
            "spaceBetweenL": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the space between slides for large viewports"
                },
                "attribute": "space-between-l",
                "reflect": false,
                "defaultValue": "0"
            },
            "spaceBetweenM": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the space between slides for medium viewports"
                },
                "attribute": "space-between-m",
                "reflect": false,
                "defaultValue": "0"
            },
            "spaceBetweenS": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the space between slides for small viewports"
                },
                "attribute": "space-between-s",
                "reflect": false,
                "defaultValue": "0"
            },
            "containerWidthXl": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the inner container width for XL viewports"
                },
                "attribute": "container-width-xl",
                "reflect": false,
                "defaultValue": "\"100%\""
            },
            "containerWidthL": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the inner container width for large viewports"
                },
                "attribute": "container-width-l",
                "reflect": false,
                "defaultValue": "\"100%\""
            },
            "containerWidthM": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the inner container width for medium viewports"
                },
                "attribute": "container-width-m",
                "reflect": false,
                "defaultValue": "\"100%\""
            },
            "containerWidthS": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the inner container width for small viewports"
                },
                "attribute": "container-width-s",
                "reflect": false,
                "defaultValue": "\"100%\""
            },
            "slidesPerGroupXl": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the slides per group of for xl viewports"
                },
                "attribute": "slides-per-group-xl",
                "reflect": false,
                "defaultValue": "1"
            },
            "slidesPerGroupL": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the slides per group of for large viewports"
                },
                "attribute": "slides-per-group-l",
                "reflect": false,
                "defaultValue": "1"
            },
            "slidesPerGroupM": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the slides per group for medium viewports"
                },
                "attribute": "slides-per-group-m",
                "reflect": false,
                "defaultValue": "1"
            },
            "slidesPerGroupS": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the slides per group for small viewports"
                },
                "attribute": "slides-per-group-s",
                "reflect": false,
                "defaultValue": "1"
            },
            "containerClasses": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "additional classes that will be added to an inner div,\nused to set utility classes that will control the internal width of the slides"
                },
                "attribute": "container-classes",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "fadedSides": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, slider will be centered, with the addition of shadows\non the sides to cut off excess slides on tablet viewports"
                },
                "attribute": "faded-sides",
                "reflect": false,
                "defaultValue": "false"
            },
            "slidesOverflowVisible": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the slides will have overflow visible, useful when the slides have outer shadow"
                },
                "attribute": "slides-overflow-visible",
                "reflect": false,
                "defaultValue": "false"
            },
            "slidesAutoHeight": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, height of the slides will be aligned with the max height of the container"
                },
                "attribute": "slides-auto-height",
                "reflect": false,
                "defaultValue": "false"
            },
            "autoHeight": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the height of the slider will be equal to the current visible slide"
                },
                "attribute": "auto-height",
                "reflect": false,
                "defaultValue": "false"
            },
            "reinitOnResize": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the slider will reinitialize itself on resize"
                },
                "attribute": "reinit-on-resize",
                "reflect": false,
                "defaultValue": "false"
            },
            "startAtRandom": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the slider will start at a random index"
                },
                "attribute": "start-at-random",
                "reflect": false,
                "defaultValue": "false"
            },
            "direction": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"vertical\" | \"horizontal\"",
                    "resolved": "\"horizontal\" | \"vertical\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the direction of the slider"
                },
                "attribute": "direction",
                "reflect": false,
                "defaultValue": "\"horizontal\""
            },
            "slidesPerColumnFill": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"row\" | \"column\"",
                    "resolved": "\"column\" | \"row\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "specifies the filling of slides on multiple row variants"
                },
                "attribute": "slides-per-column-fill",
                "reflect": false,
                "defaultValue": "\"row\""
            },
            "slidesPerColumnXl": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the slides per columns for XL viewports"
                },
                "attribute": "slides-per-column-xl",
                "reflect": false,
                "defaultValue": "1"
            },
            "slidesPerColumnL": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the slides per columns for large viewports"
                },
                "attribute": "slides-per-column-l",
                "reflect": false,
                "defaultValue": "1"
            },
            "slidesPerColumnM": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the slides per columns for medium viewports"
                },
                "attribute": "slides-per-column-m",
                "reflect": false,
                "defaultValue": "1"
            },
            "slidesPerColumnS": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "sets the slides per columns for small viewports"
                },
                "attribute": "slides-per-column-s",
                "reflect": false,
                "defaultValue": "1"
            }
        };
    }
    static get states() {
        return {
            "arrowsOffsetTop": {},
            "hasOverflowingSlides": {},
            "isBeginning": {},
            "isEnd": {},
            "options": {},
            "handleDuplicatesFlag": {}
        };
    }
    static get events() {
        return [{
                "method": "updatePagination",
                "name": "uxSliderUpdatePagination",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "event to go to previous slide"
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "sliderInitialised",
                "name": "uxSliderInitialised",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "event emitted when the slider is initialized"
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "updateSlidesPerView": {
                "complexType": {
                    "signature": "(slidesPerView: number) => Promise<void>",
                    "parameters": [{
                            "name": "slidesPerView",
                            "type": "number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Updates the number of slides per view of the slider\nit expects a number as argument",
                    "tags": []
                }
            },
            "updateSoft": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Updates the slider with the native update() method from swiper\nIt works effectively only for minor updates (size, spacing etc.)",
                    "tags": []
                }
            },
            "update": {
                "complexType": {
                    "signature": "(additionalOptions?: {}) => Promise<void>",
                    "parameters": [{
                            "name": "additionalOptions",
                            "type": "{}",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Update method to be used to reset and update the slider completely.\nIt can receive an optional argument with valid swiper options.\nSee Swiper API for more information of valid options.",
                    "tags": []
                }
            },
            "addSlide": {
                "complexType": {
                    "signature": "(slide: string | HTMLElement, before?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "slide",
                            "type": "string | HTMLElement",
                            "docs": ""
                        }, {
                            "name": "before",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLElement": {
                            "location": "global",
                            "id": "global::HTMLElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Adds the slide passed as an argument to the slider.",
                    "tags": []
                }
            },
            "removeSlide": {
                "complexType": {
                    "signature": "(index: number) => Promise<void>",
                    "parameters": [{
                            "name": "index",
                            "type": "number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Removes the slide that has the index specified as the argument from the slider",
                    "tags": []
                }
            },
            "slideNext": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "go to next slide",
                    "tags": []
                }
            },
            "slidePrev": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "go to previous slide",
                    "tags": []
                }
            },
            "slideTo": {
                "complexType": {
                    "signature": "(index: number, velocity?: number) => Promise<void>",
                    "parameters": [{
                            "name": "index",
                            "type": "number",
                            "docs": ""
                        }, {
                            "name": "velocity",
                            "type": "number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "slide to a specific slide",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "slEl"; }
    static get listeners() {
        return [{
                "name": "uxSliderStopAutoplay",
                "method": "sliderStopAutoplay",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "uxSliderResumeAutoplay",
                "method": "sliderResumeAutoplay",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "uxSliderNextSlide",
                "method": "nextSlideHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "uxSliderPreviousSlide",
                "method": "previousSlideHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=slider.js.map
